<script lang="ts">
  /**
   * WorkspaceSwitcher — dropdown pemilih workspace aktif di topbar.
   *
   * Menampilkan workspace yang sedang aktif dan memungkinkan user
   * beralih antar workspace dengan satu klik (FR-WS-02). Juga
   * menyediakan link ke halaman kelola workspace (/workspaces).
   *
   * Komponen ini membaca state reaktif dari workspace store (S7-1)
   * dan tidak memiliki props — semua data berasal dari store global.
   *
   * Acuan: docs/05-UI.md §10.3 (Topbar), docs/03-Workflow.md WF-02
   * Requirement: FR-WS-02
   */
  import { workspace, switchWorkspace } from '$lib/workspace.svelte';
  import { goto } from '$app/navigation';

  /** State dropdown terbuka/tertutup. */
  let isOpen = $state(false);

  function toggle(): void {
    isOpen = !isOpen;
  }

  function close(): void {
    isOpen = false;
  }

  /** Switch workspace lalu tutup dropdown. */
  async function handleSwitch(id: string): Promise<void> {
    await switchWorkspace(id);
    close();
  }

  /** Navigasi ke halaman kelola workspace. */
  function manageWorkspaces(): void {
    close();
    void goto('/workspaces');
  }

  /** Tutup dropdown saat tombol Escape ditekan. */
  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && isOpen) {
      close();
    }
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="ws-switcher">
  <button
    class="ws-trigger"
    onclick={toggle}
    aria-haspopup="listbox"
    aria-expanded={isOpen}
    disabled={workspace.loading}
  >
    {#if workspace.loading}
      <span class="ws-spinner" aria-label="Memuat workspace"></span>
    {:else}
      <span class="ws-icon" aria-hidden="true">🗂️</span>
    {/if}
    <span class="ws-name">{workspace.activeWorkspaceName ?? 'Memuat…'}</span>
    <span class="ws-chevron" class:open={isOpen} aria-hidden="true">▾</span>
  </button>

  {#if isOpen}
    <!-- Backdrop transparan: klik di luar dropdown untuk menutup -->
    <button class="ws-backdrop" onclick={close} tabindex="-1" aria-hidden="true"></button>

    <div class="ws-dropdown" role="listbox" aria-label="Pilih workspace">
      <div class="ws-dropdown-header">
        <span>Workspace Anda</span>
        <span class="ws-count">{workspace.count}</span>
      </div>

      <div class="ws-option-list">
        {#each workspace.workspaces as ws (ws.id)}
          <button
            class="ws-option"
            class:active={ws.id === workspace.activeWorkspaceId}
            onclick={() => handleSwitch(ws.id)}
            role="option"
            aria-selected={ws.id === workspace.activeWorkspaceId}
          >
            <span class="ws-option-icon" aria-hidden="true">🗂️</span>
            <span class="ws-option-name">{ws.name}</span>
            {#if ws.id === workspace.activeWorkspaceId}
              <span class="ws-check" aria-hidden="true">✓</span>
            {/if}
          </button>
        {/each}
      </div>

      <div class="ws-dropdown-footer">
        <button class="ws-manage-btn" onclick={manageWorkspaces}> ⚙️ Kelola Workspace </button>
      </div>
    </div>
  {/if}
</div>

<style>
  .ws-switcher {
    position: relative;
  }

  /* --- Trigger button --- */
  .ws-trigger {
    display: inline-flex;
    align-items: center;
    gap: var(--ekn-space-2);
    font-family: var(--ekn-font-sans);
    font-size: var(--ekn-text-sm);
    font-weight: var(--ekn-font-medium);
    color: var(--ekn-color-text-primary);
    background-color: var(--ekn-color-bg-surface);
    border: var(--ekn-border-1) solid var(--ekn-color-border-default);
    border-radius: var(--ekn-radius-md);
    padding: var(--ekn-space-2) var(--ekn-space-3);
    max-width: 220px;
    cursor: pointer;
    transition:
      background-color var(--ekn-transition-fast),
      border-color var(--ekn-transition-fast);
  }

  .ws-trigger:hover:not(:disabled) {
    background-color: var(--ekn-color-bg-hover);
    border-color: var(--ekn-color-border-focus);
  }

  .ws-trigger:focus-visible {
    box-shadow: var(--ekn-focus-ring);
    outline: none;
  }

  .ws-trigger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .ws-icon {
    font-size: var(--ekn-text-base);
    line-height: 1;
    flex-shrink: 0;
  }

  .ws-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ws-chevron {
    font-size: var(--ekn-text-xs);
    color: var(--ekn-color-text-secondary);
    transition: transform var(--ekn-transition-fast);
    flex-shrink: 0;
  }

  .ws-chevron.open {
    transform: rotate(180deg);
  }

  /* --- Spinner (loading state) --- */
  .ws-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--ekn-color-border-default);
    border-top-color: var(--ekn-color-action-primary);
    border-radius: 50%;
    animation: ws-spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes ws-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* --- Backdrop (click-outside) --- */
  .ws-backdrop {
    position: fixed;
    inset: 0;
    background: transparent;
    border: none;
    padding: 0;
    cursor: default;
    z-index: var(--ekn-z-dropdown);
  }

  /* --- Dropdown panel --- */
  .ws-dropdown {
    position: absolute;
    top: calc(100% + var(--ekn-space-1));
    left: 0;
    min-width: 260px;
    max-width: 320px;
    background-color: var(--ekn-color-bg-elevated);
    border: var(--ekn-border-1) solid var(--ekn-color-border-default);
    border-radius: var(--ekn-radius-lg);
    box-shadow: var(--ekn-shadow-md);
    z-index: calc(var(--ekn-z-dropdown) + 1);
    overflow: hidden;
  }

  .ws-dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ekn-space-2) var(--ekn-space-3);
    font-size: var(--ekn-text-xs);
    font-weight: var(--ekn-font-semibold);
    color: var(--ekn-color-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: var(--ekn-border-1) solid var(--ekn-color-border-subtle);
  }

  .ws-count {
    background-color: var(--ekn-color-bg-hover);
    color: var(--ekn-color-text-secondary);
    font-size: var(--ekn-text-xs);
    font-weight: var(--ekn-font-bold);
    padding: 0 var(--ekn-space-2);
    border-radius: var(--ekn-radius-full);
    min-width: 20px;
    text-align: center;
  }

  /* --- Option list --- */
  .ws-option-list {
    max-height: 280px;
    overflow-y: auto;
    padding: var(--ekn-space-1);
  }

  .ws-option {
    display: flex;
    align-items: center;
    gap: var(--ekn-space-2);
    width: 100%;
    text-align: left;
    font-family: var(--ekn-font-sans);
    font-size: var(--ekn-text-sm);
    color: var(--ekn-color-text-primary);
    background: none;
    border: none;
    border-radius: var(--ekn-radius-md);
    padding: var(--ekn-space-2) var(--ekn-space-3);
    cursor: pointer;
    transition: background-color var(--ekn-transition-fast);
  }

  .ws-option:hover {
    background-color: var(--ekn-color-bg-hover);
  }

  .ws-option:focus-visible {
    box-shadow: var(--ekn-focus-ring);
    outline: none;
  }

  .ws-option.active {
    background-color: var(--ekn-primary-50);
    font-weight: var(--ekn-font-semibold);
  }

  .ws-option-icon {
    font-size: var(--ekn-text-base);
    line-height: 1;
    flex-shrink: 0;
  }

  .ws-option-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ws-check {
    color: var(--ekn-color-action-primary);
    font-weight: var(--ekn-font-bold);
    flex-shrink: 0;
  }

  /* --- Footer --- */
  .ws-dropdown-footer {
    border-top: var(--ekn-border-1) solid var(--ekn-color-border-subtle);
    padding: var(--ekn-space-1);
  }

  .ws-manage-btn {
    display: flex;
    align-items: center;
    gap: var(--ekn-space-2);
    width: 100%;
    text-align: left;
    font-family: var(--ekn-font-sans);
    font-size: var(--ekn-text-sm);
    font-weight: var(--ekn-font-medium);
    color: var(--ekn-color-text-secondary);
    background: none;
    border: none;
    border-radius: var(--ekn-radius-md);
    padding: var(--ekn-space-2) var(--ekn-space-3);
    cursor: pointer;
    transition: background-color var(--ekn-transition-fast);
  }

  .ws-manage-btn:hover {
    background-color: var(--ekn-color-bg-hover);
    color: var(--ekn-color-text-primary);
  }

  .ws-manage-btn:focus-visible {
    box-shadow: var(--ekn-focus-ring);
    outline: none;
  }

  /* --- Responsive --- */
  @media (max-width: 640px) {
    .ws-trigger {
      max-width: 140px;
    }

    .ws-dropdown {
      min-width: 220px;
    }
  }
</style>
